import { useSetAtom } from 'jotai'
import React from 'react'
import * as R from 'remeda'
import { z } from 'zod'

import { brandGroupIdAtom } from '~/atoms'
import { exists, setValueIfExist } from '~/utils'

import {
  commentAtom,
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  groupInfosAtom,
  groupNameMapAtom,
  groupsDiffsAtom,
  hiddenColumnKeysAtom,
  hiddenRowIdsAtom,
  individualSortValueAtom,
  matrixSortValueAtom,
  questionIdsAtom,
  questionsDiffsMapAtom,
  resultViewerTypeAtom,
  savedCommentAtom,
  sequentialSortValueAtom,
} from '../../atoms'

import { schema1 } from './schemas/schema1'
import { schema2 } from './schemas/schema2'

const schema = z.union([schema1, schema2])

export const useRestoreParams = () => {
  const setBrandGroupId = useSetAtom(brandGroupIdAtom)

  const setComment = useSetAtom(commentAtom)
  const setSavedComment = useSetAtom(savedCommentAtom)
  const setDataDisplayType = useSetAtom(dataDisplayTypeAtom)
  const setGroupInfos = useSetAtom(groupInfosAtom)
  const setResultViewerType = useSetAtom(resultViewerTypeAtom)
  const setQuestionIds = useSetAtom(questionIdsAtom)
  const setCustomerAttributeIds = useSetAtom(customerAttributeIdsAtom)
  const setGroupNameMap = useSetAtom(groupNameMapAtom)

  const setSequentialSortValue = useSetAtom(sequentialSortValueAtom)
  const setIndividualSortValue = useSetAtom(individualSortValueAtom)
  const setMatrixSortValue = useSetAtom(matrixSortValueAtom)

  const setHiddenRowIds = useSetAtom(hiddenRowIdsAtom)
  const setHiddenColumnKeys = useSetAtom(hiddenColumnKeysAtom)

  const setGroupsDiffs = useSetAtom(groupsDiffsAtom)
  const setQuestionsDiffsMap = useSetAtom(questionsDiffsMapAtom)

  return React.useCallback(
    (state: unknown) => {
      const parsed = schema.safeParse(state)

      if (!parsed.success) {
        return null
      }

      const params = parsed.data

      setValueIfExist(params.brandGroupId, setBrandGroupId)
      if (exists(params.comment) && exists(params.hash)) {
        const value = params.comment
        const hash = params.hash
        setComment((prev) => ({ ...prev, [hash]: value }))
        setSavedComment((prev) => ({ ...prev, [hash]: value }))
      }
      setValueIfExist(params.dataDisplayType, setDataDisplayType)
      setValueIfExist(params.groupInfos, setGroupInfos)
      setValueIfExist(params.resultViewerType, setResultViewerType)
      setValueIfExist(params.questionIds, setQuestionIds)
      setValueIfExist(params.customerAttributeIds, setCustomerAttributeIds)
      setValueIfExist(params.groupNameMap, setGroupNameMap)
      setValueIfExist(params.sequentialSortValue, setSequentialSortValue)
      setValueIfExist(params.individualSortValue, setIndividualSortValue)
      setValueIfExist(params.matrixSortValue, setMatrixSortValue)
      setValueIfExist(params.hiddenRowIds, setHiddenRowIds)
      setValueIfExist(params.hiddenColumnKeys, setHiddenColumnKeys)
      setValueIfExist(params.groupsDiffs, setGroupsDiffs)
      setValueIfExist(params.questionsDiffsMap, setQuestionsDiffsMap)

      return R.pick(params, ['hash'])
    },
    [
      setBrandGroupId,
      setComment,
      setCustomerAttributeIds,
      setDataDisplayType,
      setGroupInfos,
      setGroupNameMap,
      setGroupsDiffs,
      setHiddenColumnKeys,
      setHiddenRowIds,
      setIndividualSortValue,
      setMatrixSortValue,
      setQuestionIds,
      setQuestionsDiffsMap,
      setResultViewerType,
      setSavedComment,
      setSequentialSortValue,
    ],
  )
}
